"use client";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import { yupResolver } from "@hookform/resolvers/yup";
import { storageUserCountry } from "@utils/default";
import { mailRegex, nameRegex } from "@utils/textUtils";
import { trimUnicodeString } from "@utils/trimText";
import { load } from "cheerio";
import { getCookie, setCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { InferType, boolean, object, string } from "yup";

import CheckBox from "@/components//ui/Form/CheckBox/CheckBox";
import { submitContactForm } from "@/components/ContactForm/actions";
import Button, { ButtonSize, ButtonTheme } from "@/components/ui/Button/Button";
import Input from "@/components/ui/Form/Input/Input";
import Telephone from "@/components/ui/Form/Telephone/Telephone";
import { LoadingContext } from "@/context/LoaderContext";
import { ModalsContext } from "@/context/ModalsContext";
import { UTMParametersContext, getFallbackCampaignParameters, utmParametersMapping } from "@/context/UTMParametersContext";
import { leadFormSubmission, mortageleadFormSubmission } from "@/dataLayer/dataLayer";
import { usePageLocationAsPerPath } from "@/hooks/usePageLocationAsPerPath";
import { getCountryNameFromCountryCode, prefixList } from "@/utils/data/prefixes";

const ContactInformationSchema = object()
  .shape({
    countryID: string(),
    firstName: string().matches(nameRegex, "form.error.firstname.invalid").required("form.error.firstname.required"),
    lastName: string().matches(nameRegex, "form.error.lastname.invalid").required("form.error.lastname.required"),
    phone: string()
      .matches(/^[0-9]+$/, "form.error.phone.invalid")
      .test("is-valid-phone", "form.error.phone.invalid", function (value) {
        const { prefix } = this.parent; // Access prefix (country code) from the parent form
        let countryCode = prefix ? prefix.split("_").find((i: string) => i[0] == "+") : "+971";
        const fullPhoneNumber = countryCode + (value || ""); // Combine the prefix and phone number

        return isValidPhoneNumber(fullPhoneNumber); // Validate with the full phone number
      })
      .required("form.error.phone.required"),
    prefix: string().required("form.error.prefix.required"),
    email: string().matches(mailRegex, "form.error.email.invalid").required("form.error.email.required"),
    newsletterSubscription: boolean(),
    privacy: boolean().required("form.error.privacy.required"),
    utmData: object({ campaignId: string().required() }),
    token: string().required("form.error.token.required"),
  })
  .required();

export type ContactInformationSchemaType = InferType<typeof ContactInformationSchema>;

type Props = {
  leadFormPageLocation?: any;
  blackTheme?: boolean;
};

export default function ContactForm({ leadFormPageLocation, blackTheme = false }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const [language, _] = locale.split("-");
  const pathname = usePathname();
  const hcaptchaRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [formSent, setFormSent] = useState<boolean>(false);
  const { additionalInfo, settings } = useContext(ModalsContext);
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const { loading } = useContext(LoadingContext);
  const { data: session } = useSession();
  const { utmParameters } = useContext(UTMParametersContext);
  const currentCountry = getCookie(storageUserCountry)?.toUpperCase() || locale.slice(3).toUpperCase();
  const defaultPrefix = prefixList.find((prefix) => prefix.code === currentCountry);

  // Trim the title to 100 charaters - start
  const locationName = usePageLocationAsPerPath(leadFormPageLocation);
  // Trim the title to 100 charaters - end

  const initialState = useMemo(() => {
    const parameters: { [key: string]: string } = {
      ...getFallbackCampaignParameters(utmParameters?.utm_medium),
      ...utmParameters,
      // lastMileConversion: `${pathname?.replaceAll("/", "") === locale ? "Home page" : document.title} Lead Form`,
      lastMileConversion: locationName,
    };

    const initialStateDefinition = {
      utmData: Object.fromEntries(Object.entries(utmParametersMapping).map(([k, v]) => [v, parameters[k] ?? ""])),
      brochureData: settings.brochureData,
      additionalInfo,
    } as LeadPayload;

    return initialStateDefinition;
  }, [additionalInfo, locale, pathname, settings.brochureData, utmParameters]);

  const handleTouchStart = () => {
    if (hcaptchaRef.current) {
      hcaptchaRef.current.execute(); // This will show a new challenge
    }
  };

  const { control, handleSubmit, formState, setValue, reset, watch } = useForm<ContactInformationSchemaType>({
    resolver: yupResolver(ContactInformationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      prefix: defaultPrefix?.dial_code + "_" + defaultPrefix?.name,
      countryID: defaultPrefix?.code ?? "AE",
      email: "",
      newsletterSubscription: false,
      // privacy: false,
      // utmData: {
      //   campaignId: "a120Y000000uLMj",
      // },
      ...initialState,
    },
    mode: "onBlur",
  });
  const currentPhoneprefix = watch("prefix");
  const privacy = watch("privacy");
  const [formFillStarted, setFormFillStarted] = useState(false);

  useEffect(() => {
    reset({ ...initialState });
  }, [initialState]);

  useEffect(() => {
    console.log("hCaptcha token received:", hcaptchaToken);
    if (hcaptchaToken) {
      console.log("hCaptcha token received:", hcaptchaToken);
      setValue("token", hcaptchaToken, { shouldValidate: true });
    }
  }, [hcaptchaToken, setValue]);

  // Step 1: Form rendered
  useEffect(() => {
    if (pathname?.includes("mortgage-calculator")) {
      // Mortgage calculator already handles this
    } else {
      leadFormSubmission({
        step_number: 1,
        step_name: "form rendered",
        form_name: "contactUs",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
    }
  }, []);

  // Step 2: Form fill started
  useEffect(() => {
    if (Object.keys(formState.touchedFields).length > 0 && !formFillStarted) {
      if (pathname?.includes("mortgage-calculator")) {
        // Mortgage calculator already handles this
      } else {
        leadFormSubmission({
          step_number: 2,
          step_name: "form fill started",
          form_name: "contactUs",
          user_logged_in: !!session,
          phone_country_code: currentPhoneprefix?.split("_")?.[0],
        });
      }
      setFormFillStarted(true);
    }
  }, [formState.touchedFields, formFillStarted, pathname, session, currentPhoneprefix]);
  const action: () => void = handleSubmit(async (data) => {
    loading(true);
    setServerError(null);
    const response = await submitContactForm({
      ...data,
      // utmData: initialState.utmData,
    });
    if (pathname?.includes("mortgage-calculator")) {
      mortageleadFormSubmission({
        step_number: 2,
        step_name: "form fill started",
        form_name: "RegisterYourInterest",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 3,
        step_name: "form fill finished",
        form_name: "RegisterYourInterest",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 4,
        step_name: "form success",
        form_name: "RegisterYourInterest",
        cta_name: "Request a callback",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 5,
        step_name: "sf lead success",
        form_name: "RegisterYourInterest",
        cta_name: "Request a callback",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
    } else {
      leadFormSubmission({
        step_number: 3,
        step_name: "form fill finished",
        form_name: "contact_us",
        user_logged_in: !!session,
        // first_name: data.firstName, // 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
        // last_name: data.lastName,
        // email: data.email,
        // phone: data.phone,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
    }

    if (response) {
      loading(false);
      console.info(response);
      if ("error" in response) {
        setValue("token", hcaptchaToken || "", { shouldValidate: true });
        setServerError(response.error || t("label.error.generic"));
      } else {
        if (pathname?.includes("mortgage-calculator")) {
          // Already handled above in mortgage calculator section
        } else {
          leadFormSubmission({
            step_number: 4,
            step_name: "form success",
            form_name: "contact_us",
            cta_name: "Request a callback",
            user_logged_in: !!session,
            phone_country_code: currentPhoneprefix?.split("_")?.[0],
          });
          leadFormSubmission({
            step_number: 5,
            step_name: "sf lead success",
            form_name: "contact_us",
            cta_name: "Request a callback",
            user_logged_in: !!session,
            phone_country_code: currentPhoneprefix?.split("_")?.[0],
          });
        }
        setFormSent(true);
        reset();
      }
    }
  });

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      Object.entries(formState.errors).forEach(([fieldName, error]) => {
        if (pathname?.includes("mortgage-calculator")) {
          mortageleadFormSubmission({
            step_number: 1,
            step_name: "form error",
            error_text: error?.message || "",
            error_field: fieldName || "",
            user_logged_in: !!session,
            phone_country_code: currentPhoneprefix?.split("_")?.[0],
            form_name: "RegisterYourInterest",
          });
        } else {
          leadFormSubmission({
            step_number: 1,
            step_name: "form error",
            error_text: error?.message || "",
            error_field: fieldName || "",
            user_logged_in: !!session,
            phone_country_code: currentPhoneprefix?.split("_")?.[0],
            form_name: "contact_us",
          });
        }
      });
    }
  }, [formState.errors, pathname, session, currentPhoneprefix]);

  return (
    <form
      ref={formRef}
      noValidate
      name="contact_us"
      onSubmit={action}
      onKeyDown={(e) => {
        // Route Enter-to-submit through requestSubmit() explicitly rather than
        // relying on the browser's native implicit submission, which can bypass
        // React's <form action> handling and throw "form unexpectedly submitted".
        if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
          e.preventDefault();
          formRef.current?.requestSubmit();
        }
      }}
    >
      <Input control={control} type="text" hidden name="countryID" />
      <div className="grid grid-cols-2 gap-xxs">
        <div className="col-span-1 pb-xxs">
          <Input control={control} name="firstName" label={`${t("form.label.firstName")}`} required reverse={!blackTheme} />
        </div>
        <div className="col-span-1 pb-xxs">
          <Input control={control} name="lastName" label={`${t("form.label.lastName")}`} required reverse={!blackTheme} />
        </div>
        <div className="col-span-2 pb-xxs">
          <Telephone
            phoneName="phone"
            prefixName="prefix"
            control={control}
            reverse={!blackTheme}
            onCountryChange={(country: string) => setValue("countryID", country)}
            controlledCountryCode={defaultPrefix?.code}
            showCountryCodeLeadPrefixes={true}
          />
        </div>
        <div className="col-span-2 pb-xxs">
          <Input control={control} name="email" label={`${t("form.label.email")}`} required reverse={!blackTheme} />
        </div>
        <div className="col-span-2 pb-xxs">
          <CheckBox control={control} className={blackTheme ? "" : "invert"} label={`${t("form.label.news")}`} name="newsletterSubscription" />
        </div>
        <div className="col-span-2 pb-xxs">
          <CheckBox
            control={control}
            className={blackTheme ? "" : "invert"}
            htmlLabel={t.rich("form.label.termAndPolicy", {
              terms: (chunks) => (
                <a className="font-medium underline" href={`/${locale}/terms`} target="_blank">
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a className="font-medium underline" href={`/${locale}/privacy-policy`} target="_blank">
                  {chunks}
                </a>
              ),
            })}
            name="privacy"
            required
          />
        </div>
      </div>
      <div onTouchStart={handleTouchStart}>
        <HCaptcha
          ref={hcaptchaRef}
          sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY!}
          onVerify={setHcaptchaToken}
          onExpire={() => setValue("token", "")}
          languageOverride={language}
        />
        {!!formState.errors.token && (
          <span className="block text-xs leading-tight font-secondary text-[#B42318] pt-xs px-xxs">
            {t(formState.errors.token.message as string)}
          </span>
        )}
      </div>
      {formSent && (
        <div
          className=" bg-status-success/30 text-white rounded w-full px-s py-s text-center static my-m"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {t("label.form.sent")}
        </div>
      )}
      {serverError && (
        <div
          className="bg-status-error/70 text-status-black rounded w-full px-s py-s text-center static my-m"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {t("label.error.generic")}
        </div>
      )}

      <Button
        type="submit"
        size={ButtonSize.MEDIUM}
        theme={blackTheme ? ButtonTheme.BLACK : ButtonTheme.WHITE}
        customClass="border-none w-full mt-s"
        isDisabled={!formState.isValid || !privacy}
      >
        {t("form.label.requestcallback")}
      </Button>
    </form>
  );
}
